import { Button, Card, FormButtonRow, IconButton } from '@ksyos/design-system';
import { useState } from 'react';
import { getLanguage } from '@common/translate/translate';

type EditableCardProps = {
    editBody: React.ReactNode;
    editFooter?: React.ReactNode;
    onCancel?: () => void;
    onSave: () => Promise<boolean>;
    readBody: React.ReactNode;
    readFooter?: React.ReactNode;
    title: string;
};

export function EditableCard(props: Readonly<EditableCardProps>) {
    const translate = getLanguage();

    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const { editBody, editFooter, onCancel, onSave, readBody, readFooter, title } = props;

    return (
        <Card>
            <Card.Transition type={isEditing ? 'edit' : 'read'} />
            <Card.Header
                action={
                    !isEditing ? (
                        <IconButton
                            icon="edit-light"
                            labelText="edit"
                            onClick={() => setIsEditing(true)}
                        />
                    ) : (
                        <IconButton
                            icon="close"
                            labelText="close"
                            onClick={() => handleCancel()}
                        />
                    )
                }
                title={title}
            />

            <Card.Body>
                {isEditing ? editBody : readBody}

                {isEditing && (
                    <FormButtonRow>
                        <Button
                            isDisabled={isSaving}
                            onClick={() => handleCancel()}
                            variant="outline"
                        >
                            {translate.common.cancel}
                        </Button>
                        <Button
                            isDisabled={isSaving}
                            onClick={handleSave}
                        >
                            {translate.common.save}
                        </Button>
                    </FormButtonRow>
                )}
            </Card.Body>

            {isEditing && editFooter && <Card.Footer>{editFooter}</Card.Footer>}
            {!isEditing && readFooter && <Card.Footer>{readFooter}</Card.Footer>}
        </Card>
    );

    function handleCancel() {
        setIsEditing(false);

        onCancel?.();
    }

    async function handleSave() {
        setIsSaving(true);

        const resumeSaving = await onSave();

        setIsSaving(false);

        if (!resumeSaving) {
            return;
        }

        setIsEditing(false);
    }
}
